import { RecommendedPrice } from './RecommendedPrice';
import { MonitoringAssortmentItemDTO, MonitoringAssortmentMgSupplierItemDTO } from '../dto';
import dayjs from 'dayjs';
import { CompetitorPrice, ItemPriority } from '@retail/products/types';
import { PriceCalculation } from '@retail/my-assortment/types';
import { minBy } from '@shared/utils';

export class MonitoringAssortmentMgSupplierItem {
  mgItemNumber: number;
  priority?: ItemPriority;
  priorityValidFrom?: string;
  primaryText: string;
  mgSupplierId: number;
  mgSupplierName: string;
  basePriceUnit?: string;
  primaryPackageUnit?: string;
  finfoNumber?: number;
  currentSalesPrices?: PriceCalculation;

  constructor({
    mgItemNumber,
    priority,
    priorityValidFrom,
    primaryText,
    mgSupplierId,
    mgSupplierName,
    basePriceUnit,
    primaryPackageUnit,
    finfoNumber,
    currentSalesPrices,
  }: MonitoringAssortmentMgSupplierItemDTO) {
    this.mgItemNumber = mgItemNumber;
    this.priority = priority;
    this.priorityValidFrom = priorityValidFrom;
    this.primaryText = primaryText;
    this.mgSupplierId = mgSupplierId;
    this.mgSupplierName = mgSupplierName;
    this.basePriceUnit = basePriceUnit;
    this.primaryPackageUnit = primaryPackageUnit;
    this.finfoNumber = finfoNumber;
    this.currentSalesPrices = PriceCalculation.fromDto(currentSalesPrices);
  }
}

export class MonitoringAssortmentItem {
  id: number;
  assortmentId: number;
  mgItemNumber: number;
  replacedByMgItemNumber?: number;
  selectedSupplierId?: number;
  mgSupplierItems: MonitoringAssortmentMgSupplierItem[];
  gtinCodes?: string[];
  recommendedPrice?: RecommendedPrice;
  competitorPrices: Record<number, CompetitorPrice[]>;

  constructor({
    id,
    assortmentId,
    mgItemNumber,
    replacedByMgItemNumber,
    selectedSupplier,
    mgSupplierItems = [],
    gtinCodes,
    recommendedPrice,
    competitorPrices,
  }: MonitoringAssortmentItemDTO) {
    this.id = id;
    this.assortmentId = assortmentId;
    this.mgItemNumber = mgItemNumber;
    this.replacedByMgItemNumber = replacedByMgItemNumber;
    this.selectedSupplierId = selectedSupplier;
    this.mgSupplierItems = mgSupplierItems?.map(
      (dto) => new MonitoringAssortmentMgSupplierItem(dto)
    );
    this.gtinCodes = gtinCodes;
    this.recommendedPrice = recommendedPrice && new RecommendedPrice(recommendedPrice);
    this.competitorPrices = competitorPrices
      ? Object.entries(competitorPrices).reduce(
          (tmp, [storeId, storePrices]) => ({
            ...tmp,
            [storeId]: storePrices.map((storePrice) => new CompetitorPrice(storePrice)),
          }),
          {}
        )
      : {};
  }

  getPrimarySupplierItem = (): MonitoringAssortmentMgSupplierItem | undefined =>
    this.mgSupplierItems.find(({ mgSupplierId }) => mgSupplierId === this.selectedSupplierId);

  getPrimaryOrLowestSalesPriceSupplierItem = (): MonitoringAssortmentMgSupplierItem | undefined =>
    this.getPrimarySupplierItem() ??
    minBy(
      this.mgSupplierItems,
      (supplierItem) =>
        supplierItem.currentSalesPrices
          ?.getBracketPackagePrice('F-PAK')
          ?.bracketMarkupPrice.getPriceForClassCode('F-PAK')?.price.priceWithVat
    );

  getCurrentCompetitorPrice = (storeId: number): CompetitorPrice | undefined =>
    this.competitorPrices[storeId]?.find((price) => price.latest);

  getPreviousCompetitorPrice = (storeId: number): CompetitorPrice | undefined => {
    const storePrices = this.competitorPrices[storeId];

    return storePrices?.reduce((prevPrice: CompetitorPrice | undefined, price) => {
      if (price.latest) {
        return prevPrice;
      }
      return prevPrice
        ? dayjs(price.lastObservedAt).isAfter(dayjs(prevPrice.lastObservedAt))
          ? price
          : prevPrice
        : price;
    }, undefined);
  };
}
